// Bucket keys from enterprise API (camelCase)
export const TIME_BUCKET_KEYS = [
    'before10Min', 'before20Min', 'before30Min', 'before40Min',
    'before50Min', 'before60Min', 'after60Min'
];

export const TIME_BUCKET_LABELS = [
    '0-10 min', '10-20 min', '20-30 min', '30-40 min',
    '40-50 min', '50-60 min', '60+ min'
];

// Lateness early keys (all sum into "ontime" bucket for distinct)
export const LATENESS_EARLY_KEYS = [
    'early45Min', 'early30Min', 'early15Min', 'early10Min', 'early5Min', 'early0Min'
];

export const LATENESS_LATE_KEYS = [
    'late5Min', 'late10Min', 'late15Min', 'late30Min', 'late45Min', 'lateOver45Min'
];

export const LATENESS_LABELS = [
    'ontime', '1 - 5', '5 - 10', '10 - 15', '15 - 30', '30 - 45', '45+'
];

export const CHART_TYPES = {
    PICKUP: 'pickup',
    DELIVERY: 'delivery',
    DRIVE: 'drive',
    LATENESS: 'lateness'
};

/**
 * Flatten the hierarchical regions → subRegions → companies structure from summary API.
 * Returns: { companyId: { summary: [...], totalDeliveries, overview } }
 */
export function flattenPerformanceSummary(rawData) {
    try {
        const result = {};
        const companies = Array.isArray(rawData) ? rawData : [];

        companies.forEach((company) => {
            const summaryArr = company.summary || [];
            const totalDeliveries = summaryArr.reduce(
                (sum, day) => sum + (day.completedDeliveries || 0), 0
            );
            const overview = calculateCompanyOverview(summaryArr, totalDeliveries);

            result[company.companyId] = {
                companyId: company.companyId,
                summary: summaryArr,
                totalDeliveries,
                overview
            };
        });

        return result;
    } catch (e) {
        console.error('flattenPerformanceSummary error:', e);
        return {};
    }
}

/**
 * Calculate a company's aggregated overview metrics from daily summary data.
 */
function calculateCompanyOverview(summaryArr, totalDeliveries) {
    if (!summaryArr || summaryArr.length === 0 || totalDeliveries === 0) {
        return {
            completedDeliveries: 0,
            onTimeDeliveries: 0,
            avgPromiseTime: 0,
            avgPlacementToPickupTime: 0,
            avgPickupToDeliveryTime: 0,
            avgPlacementToDeliveryTime: 0,
            pickupBefore20Min: 0,
            deliveryBefore40Min: 0
        };
    }

    let weightedOnTime = 0;
    let weightedPromise = 0;
    let weightedPickup = 0;
    let weightedDrive = 0;
    let weightedDelivery = 0;
    let weightedPickup20 = 0;
    let weightedDelivery40 = 0;

    summaryArr.forEach(day => {
        const count = day.completedDeliveries || 0;
        if (count > 0) {
            weightedOnTime += (day.onTimeDeliveries || 0) * count;
            weightedPromise += (day.avgPromiseTime || 0) * count;
            weightedPickup += (day.avgPlacementToPickupTime || 0) * count;
            weightedDrive += (day.avgPickupToDeliveryTime || 0) * count;
            weightedDelivery += (day.avgPlacementToDeliveryTime || 0) * count;
            weightedPickup20 += (day.pickupBefore20Min || 0) * count;
            weightedDelivery40 += (day.deliveryBefore40Min || 0) * count;
        }
    });

    return {
        completedDeliveries: totalDeliveries,
        onTimeDeliveries: weightedOnTime / totalDeliveries,
        avgPromiseTime: weightedPromise / totalDeliveries,
        avgPlacementToPickupTime: weightedPickup / totalDeliveries,
        avgPickupToDeliveryTime: weightedDrive / totalDeliveries,
        avgPlacementToDeliveryTime: weightedDelivery / totalDeliveries,
        pickupBefore20Min: weightedPickup20 / totalDeliveries,
        deliveryBefore40Min: weightedDelivery40 / totalDeliveries
    };
}

/**
 * Flatten the hierarchical structure from all 4 typed report APIs.
 * Input: { pickup: rawData, delivery: rawData, drive: rawData, lateness: rawData }
 * Returns: { companyId: { pickup: {distinct, cumulative}, delivery: {...}, ... } }
 */
export function flattenPerformanceCharts(chartDataMap) {
    try {
        const result = {};

        Object.keys(chartDataMap).forEach(chartType => {
            const rawData = chartDataMap[chartType];
            const companies = Array.isArray(rawData) ? rawData : [];

            companies.forEach((company) => {
                if (!result[company.companyId]) {
                    result[company.companyId] = {};
                }
                result[company.companyId][chartType] = company.report || { distinct: [], cumulative: [] };
            });
        });

        return result;
    } catch (e) {
        console.error('flattenPerformanceCharts error:', e);
        return {};
    }
}

/**
 * Aggregate bucket data across companies (weighted by order count).
 * Returns: { barData: number[], lineData: number[] }
 */
export function aggregateChartBuckets(companyCharts, chartType, companySummaries, companyIdArr = null) {
    try {
        const targetCompanies = companyIdArr
            ? companyIdArr.map(String)
            : Object.keys(companyCharts);

        const isLateness = chartType === CHART_TYPES.LATENESS;
        const bucketCount = 7;

        let totalOrders = 0;
        const weightedDistinct = new Array(bucketCount).fill(0);
        const weightedCumulative = new Array(bucketCount).fill(0);

        targetCompanies.forEach(companyId => {
            const chartData = companyCharts[companyId]?.[chartType];
            const summaryData = companySummaries[companyId];
            if (!chartData || !summaryData) return;

            const orders = summaryData.totalDeliveries || 0;
            if (orders === 0) return;

            const distinctObj = chartData.distinct?.[0];
            const cumulativeObj = chartData.cumulative?.[0];
            if (!distinctObj && !cumulativeObj) return;

            totalOrders += orders;

            const distinctArr = isLateness
                ? extractLatenessBuckets(distinctObj, false)
                : extractTimeBuckets(distinctObj);
            const cumulativeArr = isLateness
                ? extractLatenessBuckets(cumulativeObj, true)
                : extractTimeBuckets(cumulativeObj);

            for (let i = 0; i < bucketCount; i++) {
                weightedDistinct[i] += (distinctArr[i] || 0) * orders;
                weightedCumulative[i] += (cumulativeArr[i] || 0) * orders;
            }
        });

        if (totalOrders === 0) {
            return {
                barData: new Array(bucketCount).fill(0),
                lineData: new Array(bucketCount).fill(0)
            };
        }

        return {
            barData: weightedDistinct.map(v => v / totalOrders),
            lineData: weightedCumulative.map(v => v / totalOrders)
        };
    } catch (e) {
        console.error('aggregateChartBuckets error:', e);
        return {
            barData: new Array(7).fill(0),
            lineData: new Array(7).fill(0)
        };
    }
}

/**
 * Extract time bucket values (pickup, delivery, drive) from a raw bucket object.
 */
function extractTimeBuckets(obj) {
    if (!obj) return new Array(7).fill(0);
    return TIME_BUCKET_KEYS.map(key => parseFloat(obj[key]) || 0);
}

/**
 * Extract lateness bucket values from a raw bucket object.
 * For distinct: sum all early_* keys into "ontime" bucket
 * For cumulative: use early5Min as the "ontime" cumulative value
 */
function extractLatenessBuckets(obj, isCumulative) {
    if (!obj) return new Array(7).fill(0);

    let ontimeValue = 0;
    if (isCumulative) {
        // For cumulative, the "ontime" point = early0Min cumulative (all early deliveries)
        ontimeValue = parseFloat(obj['early0Min']) || 0;
    } else {
        // For distinct, sum all early keys into single "ontime" bucket
        LATENESS_EARLY_KEYS.forEach(key => {
            ontimeValue += parseFloat(obj[key]) || 0;
        });
    }

    const result = [ontimeValue];
    LATENESS_LATE_KEYS.forEach(key => {
        result.push(parseFloat(obj[key]) || 0);
    });

    return result;
}
